import { FC, useCallback, useMemo, useState } from 'react';

import styled from 'styled-components';
import MessageBar from '@splunk/react-ui/MessageBar';
import { variables } from '@splunk/themes';

import { react2angular } from 'react2angular';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../../common/theme/ThemeProvider';
import Dropdown, {
    DropdownPossibleCloseReason,
    DropdownRequestCloseHandler,
} from '@splunk/react-ui/Dropdown';
import Menu from '@splunk/react-ui/Menu';
import { PersistedMutingRule } from './mutingRule.model';
import { DetectorMutingRuleItem } from './DetectorMutingRuleItem';
import SlidingPanels from '@splunk/react-ui/SlidingPanels';
import ChevronLeft from '@splunk/react-icons/ChevronLeft';
import { AngularInjector } from '../../../common/AngularUtils';
import P from '@splunk/react-ui/Paragraph';
import List from '@splunk/react-ui/List';
import { ConfirmationModal } from '@splunk/olly-common/ConfirmationModal';

type DetectorMutingRuleBarProps = {
    mutings: Array<PersistedMutingRule>;
    handleDeleteMuting: (muting: PersistedMutingRule) => void;
    handleResumeMuting: (muting: PersistedMutingRule) => void;
    handleEditMuting: (muting: PersistedMutingRule) => void;
    handleReadableMutingRuleLinkClick: () => void;
    reloadMutings: () => void;
    editable: boolean;
    readable: boolean;
};

const StyledMenu = styled(Menu)`
    max-width: 300px;
    width: 300px;
`;

const StyledMessageBar = styled(MessageBar)`
    margin-bottom: 0;
    padding: ${variables.spacingMedium} ${variables.spacingSmall};
    & > svg {
        height: 16px;
    }
    & div[data-test='content'] {
        white-space: nowrap;
    }
`;

const StyledTextSpan = styled.span`
    text-decoration: underline;
    cursor: pointer;
    font-weight: ${variables.fontWeightBold};
`;

const StyledList = styled(List)`
    margin-top: 20px;
    margin-bottom: 20px;
`;

const StyledActionMenuItem = styled(Menu.Item)`
    color: ${variables.statusColorCritical};
`;

const StyledModal = styled(ConfirmationModal)`
    width: 612px;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;

    [data-test='body'] {
        padding: ${variables.spacingXLarge};
    }
`;

const closeReasons: DropdownPossibleCloseReason[] = [
    'clickAway',
    'escapeKey',
    'offScreen',
    'toggleClick',
];

const DELETE_LABELS = {
    title: 'Delete muting rule',
    confirmButton: 'Delete',
    cancelButton: 'Cancel',
    description: 'Are you sure you want to delete the following muting rule?',
    disclaimer: "Note that the muting rule can't be recovered after deletion.",
};

const RESUME_LABELS = {
    title: 'Resume notifications',
    confirmButton: 'Resume notifications',
    cancelButton: 'Cancel',
    description: 'Are you sure you want to resume notification and end the following muting rule?',
    disclaimer: 'Note that the muting rule will be deactivated permanently.',
};

const DetectorMutingRuleBar: FC<DetectorMutingRuleBarProps> = ({
    mutings,
    editable,
    reloadMutings,
    readable,
    handleReadableMutingRuleLinkClick,
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [transition, setTransition] = useState<'forward' | 'backward'>('forward');
    const [activePanelId, setActivePanelId] = useState<'muting' | 'action'>('muting');

    const [selectedMuting, setSelectedMuting] = useState<PersistedMutingRule>();

    const mutingService = AngularInjector.useInjectedClass('mutingService');

    const themeKey = useLegacyThemeServiceKey();

    const mutingText =
        'Notifications for one or more rules in this detector are currently muted or scheduled to be muted.';
    const viewMutingRuleLinkLabel = 'View muting rules.';

    const toggle = <StyledTextSpan>{viewMutingRuleLinkLabel}</StyledTextSpan>;

    const handleMutingChange = useCallback((muting) => {
        setSelectedMuting(muting);
        setTransition('forward');
        setActivePanelId('action');
    }, []);

    const handleResumeDeleteAction = useCallback((): void => {
        setDropdownOpen(false);
        setOpenModal(true);
    }, []);

    const handleModalAction = useCallback(() => {
        setOpenModal(false);
        if (selectedMuting && selectedMuting.started) {
            mutingService.endMuting(selectedMuting).then(reloadMutings);
        } else if (selectedMuting && !selectedMuting.started) {
            mutingService.deleteMuting(selectedMuting).then(reloadMutings);
        }
    }, [mutingService, reloadMutings, selectedMuting]);

    const handleEditAction = useCallback(() => {
        setDropdownOpen(false);
        mutingService.openMutingModal(selectedMuting, []).then(reloadMutings);
    }, [mutingService, reloadMutings, selectedMuting]);

    const handleRequestClose: DropdownRequestCloseHandler = useCallback(({ reason }): void => {
        if (closeReasons.includes(reason)) {
            setDropdownOpen(false);
        }
    }, []);

    const { title, confirmButton, cancelButton, description, disclaimer } = useMemo(() => {
        if (selectedMuting) {
            return selectedMuting.started ? RESUME_LABELS : DELETE_LABELS;
        }
        return { title: '', confirmButton: '', cancelButton: '', description: '', disclaimer: '' };
    }, [selectedMuting]);

    return (
        <ThemeProvider colorScheme={themeKey}>
            <StyledMessageBar type="info" aria-labelledby={mutingText}>
                {mutingText}{' '}
                {readable && editable && (
                    <Dropdown
                        open={dropdownOpen}
                        onRequestClose={handleRequestClose}
                        onRequestOpen={(): void => {
                            setActivePanelId('muting');
                            setSelectedMuting(undefined);
                            setDropdownOpen(true);
                        }}
                        closeReasons={closeReasons}
                        toggle={toggle}
                    >
                        <SlidingPanels activePanelId={activePanelId} transition={transition}>
                            <SlidingPanels.Panel key="muting" panelId="muting">
                                <StyledMenu>
                                    {mutings.map((muting) => (
                                        <DetectorMutingRuleItem
                                            key={muting.id}
                                            muting={muting}
                                            handleMutingChange={handleMutingChange}
                                        />
                                    ))}
                                </StyledMenu>
                            </SlidingPanels.Panel>
                            <SlidingPanels.Panel key="action" panelId="action">
                                <StyledMenu>
                                    <Menu.Item
                                        onClick={(): void => {
                                            setTransition('backward');
                                            setActivePanelId('muting');
                                            setSelectedMuting(undefined);
                                        }}
                                    >
                                        <ChevronLeft /> Back
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item onClick={handleEditAction}>Edit</Menu.Item>
                                    <StyledActionMenuItem onClick={handleResumeDeleteAction}>
                                        {selectedMuting?.started
                                            ? 'Resume Notifications'
                                            : 'Delete'}
                                    </StyledActionMenuItem>
                                </StyledMenu>
                            </SlidingPanels.Panel>
                        </SlidingPanels>
                    </Dropdown>
                )}
                {readable && !editable && (
                    <StyledTextSpan onClick={handleReadableMutingRuleLinkClick}>
                        {viewMutingRuleLinkLabel}
                    </StyledTextSpan>
                )}
            </StyledMessageBar>
            <StyledModal
                isOpen={openModal}
                title={title}
                confirmationButtonMessage={confirmButton}
                cancelButtonMessage={cancelButton}
                onConfirm={handleModalAction}
                onCancel={(): void => setOpenModal(false)}
            >
                <P>{description}</P>
                <StyledList>
                    {selectedMuting && <List.Item>{selectedMuting.description}</List.Item>}
                </StyledList>
                <P>{disclaimer}</P>
            </StyledModal>
        </ThemeProvider>
    );
};

export const detectorMutingRuleBar = react2angular(DetectorMutingRuleBar, [
    'mutings',
    'reloadMutings',
    'editable',
    'readable',
    'handleReadableMutingRuleLinkClick',
]);
